import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { API_BASE } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Check, X, Car, Bike } from "lucide-react";

export default function WalkinApprove() {
    const { id } = useParams();
    const { user, loading } = useAuth();
    const [walkin, setWalkin] = useState(null);
    const [err, setErr] = useState("");
    const [busy, setBusy] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        if (loading) return;
        if (!user) { navigate("/", { replace: true, state: { next: `/walkin/${id}` } }); return; }
        api.get(`/walkins/${id}`).then((r) => setWalkin(r.data)).catch((e) => setErr(e?.response?.data?.detail || "Not found"));
    }, [id, user, loading, navigate]);

    const decide = async (decision) => {
        setBusy(true);
        try {
            const r = await api.post(`/walkins/${id}/decision`, { decision });
            setWalkin(r.data);
            toast.success(decision === "approved" ? "Visitor approved" : "Visitor rejected");
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
        finally { setBusy(false); }
    };

    if (loading || (!walkin && !err)) return <div className="min-h-screen grid place-items-center bg-nest-base"><div className="h-8 w-8 rounded-full border-2 border-nest-clay border-t-transparent animate-spin"/></div>;
    if (err) return <div className="min-h-screen grid place-items-center bg-nest-base text-nest-ink2" data-testid="walkin-error">{err}</div>;

    const photoUrl = `${API_BASE}/walkins/${walkin.walkin_id}/photo`;

    return (
        <div className="min-h-screen bg-nest-base py-10 px-5">
            <div className="max-w-xl mx-auto" data-testid="walkin-approve-page">
                <p className="nest-stat-label">Approval needed</p>
                <h1 className="font-display text-3xl lg:text-4xl tracking-tighter mt-2">Someone at your gate</h1>
                <p className="text-nest-ink2 mt-2">The guard captured this photo. Approve to let them in.</p>

                <div className="nest-card mt-8 overflow-hidden">
                    <img src={photoUrl} alt="Visitor" className="w-full h-80 object-cover bg-nest-alt" data-testid="walkin-photo"/>
                    <div className="p-6">
                        <h2 className="font-display text-2xl tracking-tight">{walkin.name}</h2>
                        <p className="text-nest-ink2 mt-1">{walkin.purpose}{walkin.phone ? ` · ${walkin.phone}` : ""}</p>
                        {walkin.vehicle_no && (
                            <div className="flex items-center gap-1.5 mt-2.5">
                                <span className="inline-flex items-center gap-1.5 font-mono text-xs font-semibold px-2.5 py-1 rounded-md bg-nest-alt border border-nest-line text-nest-ink">
                                    {walkin.vehicle_type === "bike" ? <Bike size={13} className="text-nest-clay" /> : <Car size={13} className="text-nest-clay" />}
                                    <span>{walkin.vehicle_no}</span>
                                </span>
                            </div>
                        )}
                        <p className="text-xs text-nest-ink3 mt-2">For flat {walkin.tower}-{walkin.flat_no} · logged by {walkin.guard_name}</p>
                        <p className="text-xs text-nest-ink3 mt-1">{new Date(walkin.created_at).toLocaleString()}</p>

                        {walkin.status === "pending" ? (
                            <div className="mt-6 grid grid-cols-2 gap-3">
                                <button onClick={() => decide("rejected")} disabled={busy} className="py-4 rounded-xl border-2 border-nest-err text-nest-err font-semibold hover:bg-nest-err hover:text-white transition flex items-center justify-center gap-2" data-testid="walkin-reject-btn"><X size={18}/> Reject</button>
                                <button onClick={() => decide("approved")} disabled={busy} className="py-4 rounded-xl bg-nest-sage text-white font-semibold hover:bg-nest-sageDark transition flex items-center justify-center gap-2" data-testid="walkin-approve-btn"><Check size={18}/> Approve entry</button>
                            </div>
                        ) : (
                            <div className={`mt-6 p-4 rounded-xl text-center font-semibold ${walkin.status === "approved" || walkin.status === "entered" ? "bg-nest-sage text-white" : "bg-nest-err text-white"}`} data-testid="walkin-status-final">
                                {walkin.status === "entered" ? "Visitor has entered" : walkin.status === "approved" ? "Approved" : "Rejected"}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
